import React, { useEffect, useRef, useState } from 'react';

import { TotemIconId } from '@meilleurtaux/mtp-uikit-core';
import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';

import useResizeObserver from '@/hooks/useResizeObserver';

import styles from './styles.module.css';

interface AccessAccordionProps {
  id: string;
  title: string;
  subtitle: string;
  icon: TotemIconId;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const AccessAccordion: React.FC<AccessAccordionProps> = ({ id, title, subtitle, icon, isOpen, onToggle, children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const panelId = `accordion-panel-${id}`;
  const headerId = `accordion-header-${id}`;

  useEffect(() => {
    if (!isOpen) {
      setPanelHeight(0);
      return;
    }

    const element = contentRef.current;
    if (!element) return;

    setPanelHeight(element.scrollHeight);
  }, [isOpen]);

  useResizeObserver(
    contentRef,
    () => {
      if (contentRef.current) setPanelHeight(contentRef.current.scrollHeight);
    },
    isOpen
  );

  return (
    <section className={`${styles.accordion} ${isOpen ? styles.accordionOpen : ''}`}>
      <button
        id={headerId}
        className={styles.accordionHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        type="button"
      >
        <div className={styles.accordionHeaderContent}>
          <div className={styles.iconWrapper}>
            <MtxIcon name={icon} size={24} />
          </div>
          <div className={styles.accordionTitles}>
            <span className={styles.accordionTitle}>{title}</span>
            <span className={styles.accordionSubtitle}>{subtitle}</span>
          </div>
        </div>
        <div className={styles.chevronZone}>
          <MtxIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} name="chevron-down" size={28} />
        </div>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        aria-hidden={!isOpen}
        className={`${styles.accordionPanel} ${isOpen ? styles.accordionPanelOpen : ''}`}
        ref={contentRef}
        style={{
          maxHeight: panelHeight,
        }}
      >
        <div className={styles.accordionContent}>{children}</div>
      </div>
    </section>
  );
};

export default AccessAccordion;
